import { useMemo, useState } from 'react';
import { Dropdown, Popover } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ChevronRightIcon } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { getFormattedTime } from '@/common/utils/getFormattedTimeUtils';
import { getUserTimeZoneValue, parseShipmentDayjs } from '@/common/utils/utils';
import SensorTimelineModal from '@/pages/Shipments/SensorTimeline/SensorTimelineModal';
import {
  getVitalDisplayUnit,
  type VitalModalType,
} from '../../../partials/sensorVitals';
import type { ShipmentCellValue, ShipmentRow } from '../../types';
import {
  SENSOR_COLUMN_VITAL_TYPES,
  VITAL_EXCURSION_ALERT_TYPES,
} from './shipmentTableConstants';
import { formatValue } from './shipmentTableUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const formatGmtLabel = (offsetMinutes: number): string => {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return `GMT${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

interface SensorReading {
  timestamp: string;
  value: string;
}

interface SensorEntry {
  sensor_id: string;
  values: SensorReading[];
}

interface PopoverRow {
  key: string;
  timeLabel: string;
  value: string;
  isOutOfRange: boolean;
}

interface VitalRange {
  min: number | null;
  max: number | null;
}

const MAX_ROWS = 10;

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isNaN(num) ? null : num;
};

/**
 * Per-vital "Product requirements" range for this shipment. temperature/
 * humidity/pressure have a real min and max; shock/light only have a max
 * (single upper bound, no min makes sense for either). Battery has no
 * threshold field anywhere, so it always resolves to {min: null, max:
 * null} -- readings for it are never flagged out of range here.
 */
const getVitalRange = (
  row: ShipmentRow,
  vitalType: VitalModalType | undefined
): VitalRange => {
  switch (vitalType) {
    case 'temperature':
      return {
        min: toNumberOrNull(row.temperature_min),
        max: toNumberOrNull(row.temperature_max),
      };
    case 'humidity':
      return {
        min: toNumberOrNull(row.humidity_min),
        max: toNumberOrNull(row.humidity_max),
      };
    case 'pressure':
      return {
        min: toNumberOrNull(row.pressure_min),
        max: toNumberOrNull(row.pressure_max),
      };
    case 'shock_value':
      return { min: null, max: toNumberOrNull(row.shock) };
    case 'light':
      return { min: null, max: toNumberOrNull(row.light) };
    case 'battery':
    case 'tilt':
    case 'cell_signal':
    case undefined:
      return { min: null, max: null };
  }
};

const isReadingOutOfRange = (
  value: string | number | null | undefined,
  range: VitalRange
): boolean => {
  if (range.min === null && range.max === null) return false;
  const num = toNumberOrNull(value);
  if (num === null) return false;
  if (range.min !== null && num < range.min) return true;
  if (range.max !== null && num > range.max) return true;
  return false;
};

/**
 * Format a vital's raw value for display. Temperature is formatted to
 * exactly 2 decimal places; other vitals pass through unchanged for now.
 */
const formatVitalValue = (
  value: string | number | null | undefined,
  vitalType: VitalModalType | undefined
): string => {
  if (value === null || value === undefined || value === '') return '';
  if (vitalType === 'temperature') {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isNaN(num)) return num.toFixed(2);
  }
  return String(value);
};

/**
 * Build the popover's reading list for the currently active sensor: the
 * most recent raw readings only, capped to MAX_ROWS, each individually
 * flagged red when it falls outside the vital's range (no range for the
 * vital, e.g. battery -- never flagged).
 */
export const buildPopoverRows = ({
  readings,
  offsetMinutes,
  vitalType,
  range = { min: null, max: null },
}: {
  readings: SensorReading[];
  offsetMinutes: number;
  vitalType?: VitalModalType;
  range?: VitalRange;
}): PopoverRow[] => {
  const readingsDesc = sortReadingsDesc(readings);

  const rows: PopoverRow[] = readingsDesc.map((reading, index) => {
    const time = dayjs.utc(reading.timestamp).utcOffset(offsetMinutes);
    return {
      key: `${reading.timestamp}-${index}`,
      timeLabel: time.isValid() ? time.format('HH:mm:ss') : reading.timestamp,
      value: formatVitalValue(reading.value, vitalType),
      isOutOfRange: isReadingOutOfRange(reading.value, range),
    };
  });

  return rows.slice(0, MAX_ROWS);
};

const parseSensorEntries = (raw: unknown): SensorEntry[] => {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is SensorEntry =>
        Boolean(entry) &&
        typeof entry === 'object' &&
        typeof entry.sensor_id === 'string' &&
        Array.isArray(entry.values)
    );
  } catch {
    return [];
  }
};

const sortReadingsDesc = (values: SensorReading[]): SensorReading[] =>
  [...values].sort(
    (a, b) =>
      dayjs.utc(b.timestamp).valueOf() - dayjs.utc(a.timestamp).valueOf()
  );

// sensor_name is one value for the whole shipment, not one per sensor —
// a shipment with multiple sensors only ever has a single recorded name.
// Apply it to every entry with a per-entry counter instead of looking up
// a name per sensor.
const getSensorNameLabel = (row: ShipmentRow): string => {
  const raw = typeof row.sensor_name === 'string' ? row.sensor_name : '';
  return raw.split('|')[0]?.trim() || 'Sensor';
};

interface ShipmentSensorCellProps {
  value: ShipmentCellValue;
  row: ShipmentRow;
  columnKey: string;
}

const ShipmentSensorCell = ({
  value,
  row,
  columnKey,
}: ShipmentSensorCellProps) => {
  const entries = useMemo(() => parseSensorEntries(value), [value]);
  const sensorNameLabel = useMemo(() => getSensorNameLabel(row), [row]);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;

  const displayText = formatValue(value);
  const vitalType = SENSOR_COLUMN_VITAL_TYPES[columnKey];
  const unit = vitalType ? getVitalDisplayUnit(vitalType) : '';

  // The cell's value shows the excursion's own value when there's an open
  // excursion alert for this vital, falling back to the plain latest
  // reading otherwise. The label/color follow the same open-alert check.
  const excursionAlertType = vitalType
    ? VITAL_EXCURSION_ALERT_TYPES[vitalType]
    : undefined;
  const excursionMatches = excursionAlertType
    ? (row.alert_details ?? []).filter(
        (alert) => alert.alert_type === excursionAlertType
      )
    : [];
  const mostRecentExcursion =
    excursionMatches.length > 0
      ? [...excursionMatches].sort(
          (a, b) =>
            (parseShipmentDayjs(b.occurred_at)?.valueOf() ?? 0) -
            (parseShipmentDayjs(a.occurred_at)?.valueOf() ?? 0)
        )[0]
      : null;
  // Only trust the deduped distinct-sensor count when every match has a
  // shipment_sensor_id -- if even one is missing, we can't tell whether it's
  // a duplicate of a known sensor or a distinct one, so fall back to the raw
  // match count rather than risk silently undercounting.
  const excursionSensorIds = excursionMatches.map(
    (alert) => alert.shipment_sensor_id
  );
  const outOfRangeSensorCount = excursionSensorIds.every(Boolean)
    ? new Set(excursionSensorIds).size
    : excursionMatches.length;
  const excursionTotalSensors = Math.max(entries.length, outOfRangeSensorCount);
  const excursionLabel =
    excursionTotalSensors > 1
      ? `${outOfRangeSensorCount} of ${excursionTotalSensors} out of range`
      : 'Out of range';
  if (entries.length === 0) {
    return <span className='shipment-grid-plain-cell'>{displayText}</span>;
  }

  const activeSensorId =
    selectedSensorId &&
    entries.some((entry) => entry.sensor_id === selectedSensorId)
      ? selectedSensorId
      : entries[0].sensor_id;
  const activeEntry =
    entries.find((entry) => entry.sensor_id === activeSensorId) ?? entries[0];

  // One offset (the viewer's current offset), applied uniformly to every
  // reading regardless of the reading's own date — not a per-date
  // historical conversion. Respects the same time_zone_enable preference
  // used for the relative-time label below.
  const targetTimeZoneName = getUserTimeZoneValue(
    decodedUserToken() ?? undefined
  );
  const offsetMinutes = dayjs().tz(targetTimeZoneName).utcOffset();
  const tzLabel = formatGmtLabel(offsetMinutes);
  const activeEntryReadingsDesc = sortReadingsDesc(activeEntry.values);
  const latestDate = activeEntryReadingsDesc[0]
    ? dayjs.utc(activeEntryReadingsDesc[0].timestamp).utcOffset(offsetMinutes)
    : null;
  const dateLabel = latestDate?.isValid()
    ? latestDate.format('DD MMM YYYY')
    : '';
  const shipmentId = String(row.id ?? row.shipment_id ?? '');

  const vitalRange = getVitalRange(row, vitalType);
  const rows = buildPopoverRows({
    readings: activeEntry.values,
    offsetMinutes,
    vitalType,
    range: vitalRange,
  });

  const allReadingsDesc = sortReadingsDesc(
    entries.flatMap((entry) => entry.values)
  );
  const mostRecentReading = allReadingsDesc[0];
  const relativeTime = mostRecentReading
    ? getFormattedTime(mostRecentReading.timestamp, isTimeZone).relative
    : '';
  const sensorCountSuffix =
    entries.length > 1 ? ` · ${entries.length} sensors` : '';

  // The displayed excursion value/time is whichever is more recent -- the
  // open alert's own recorded value, or the most recent reading (across
  // all sensors) that's itself out of range per the vital's threshold.
  // An alert can go stale (still open, but the sensor has since moved on)
  // while a fresh out-of-range reading hasn't triggered its own alert yet
  // -- this surfaces whichever data point is actually the newest signal.
  // Red/"Out of range" label gating and the N-of-M count are unaffected
  // -- still purely "is there an open alert."
  const mostRecentOutOfRangeReading = allReadingsDesc.find((reading) =>
    isReadingOutOfRange(reading.value, vitalRange)
  );
  const alertExcursionTime = mostRecentExcursion
    ? (parseShipmentDayjs(mostRecentExcursion.occurred_at)?.valueOf() ?? -1)
    : -1;
  const readingExcursionTime = mostRecentOutOfRangeReading
    ? dayjs.utc(mostRecentOutOfRangeReading.timestamp).valueOf()
    : -1;
  const useReadingExcursion =
    Boolean(mostRecentOutOfRangeReading) &&
    readingExcursionTime > alertExcursionTime;
  const displayedExcursionValue =
    useReadingExcursion && mostRecentOutOfRangeReading
      ? mostRecentOutOfRangeReading.value
      : mostRecentExcursion?.current_value;
  const excursionRelativeTime =
    useReadingExcursion && mostRecentOutOfRangeReading
      ? getFormattedTime(mostRecentOutOfRangeReading.timestamp, isTimeZone)
          .relative
      : mostRecentExcursion
        ? getFormattedTime(mostRecentExcursion.occurred_at ?? '', isTimeZone)
            .relative
        : '';

  const cardContent = (
    <div className='shipment-sensor-popover-card'>
      {entries.length > 1 ? (
        <Dropdown
          trigger={['click']}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentElement ?? triggerNode
          }
          classNames={{ root: 'shipment-sensor-popover-select-dropdown' }}
          menu={{
            selectedKeys: [activeSensorId],
            items: entries.map((entry) => ({
              key: entry.sensor_id,
              label: `${sensorNameLabel}: ${entry.sensor_id}`,
            })),
            onClick: ({ key }) => setSelectedSensorId(String(key)),
          }}
        >
          <button
            type='button'
            className='shipment-sensor-popover-select'
            onClick={(event) => event.stopPropagation()}
          >
            <span>
              {sensorNameLabel}: {activeSensorId}
            </span>
            <ChevronRightIcon
              width={16}
              height={16}
              fill='#fff'
              className='shipment-sensor-popover-select-chevron'
            />
          </button>
        </Dropdown>
      ) : null}
      <div className='shipment-sensor-popover-header'>
        {entries.length <= 1 ? (
          // Redundant when the dropdown trigger above already shows
          // "{sensorNameLabel}: {activeSensorId}" in matching styling.
          <p className='shipment-sensor-popover-title'>
            Sensor: {activeSensorId}
          </p>
        ) : null}
        <p className='shipment-sensor-popover-subtitle'>
          {[dateLabel, unit, tzLabel].filter(Boolean).join(' ∙ ')}
        </p>
      </div>
      <div className='shipment-sensor-popover-rows'>
        {rows.map((popoverRow) => (
          <div key={popoverRow.key}>
            <div
              className={
                popoverRow.isOutOfRange
                  ? 'shipment-sensor-popover-row shipment-sensor-popover-row--out-of-range'
                  : 'shipment-sensor-popover-row'
              }
            >
              <span className='shipment-sensor-popover-time'>
                {popoverRow.timeLabel}
              </span>
              <span className='shipment-sensor-popover-value'>
                {popoverRow.value}
              </span>
            </div>
            <div className='shipment-sensor-popover-divider' />
          </div>
        ))}
      </div>
      {vitalType && shipmentId ? (
        <button
          type='button'
          className='shipment-sensor-popover-footer'
          onClick={(event) => {
            event.stopPropagation();
            setModalOpen(true);
          }}
        >
          See full sensor chart &rarr;
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      <Popover
        arrow={false}
        placement='top'
        trigger={['hover', 'focus']}
        classNames={{
          root: 'shipment-sensor-popover',
          container: 'shipment-sensor-popover-container',
        }}
        content={cardContent}
      >
        {mostRecentReading ? (
          <span
            className={
              mostRecentExcursion
                ? 'shipment-sensor-cell-summary shipment-sensor-cell-summary--excursion'
                : 'shipment-sensor-cell-summary'
            }
          >
            <span className='shipment-sensor-cell-value'>
              {formatVitalValue(
                mostRecentExcursion
                  ? displayedExcursionValue
                  : mostRecentReading.value,
                vitalType
              )}{' '}
              {unit.trim()}
            </span>
            <span className='shipment-sensor-cell-meta'>
              {mostRecentExcursion
                ? [excursionLabel, excursionRelativeTime]
                    .filter(Boolean)
                    .join(' ')
                : `${relativeTime}${sensorCountSuffix}`}
            </span>
          </span>
        ) : (
          <span className='shipment-grid-plain-cell'>{displayText}</span>
        )}
      </Popover>
      {shipmentId && modalOpen ? (
        <SensorTimelineModal
          shipmentId={shipmentId}
          focus={vitalType}
          onClose={() => setModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ShipmentSensorCell;
